import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { userApi } from '@/services/api';

interface UserState {
  username: string;
  userInfo: unknown | null;
  userSettings: Record<string, unknown>;

  setUsername: (username: string) => void;
  setUserInfo: (info: unknown | null) => void;
  setUserSettings: (settings: Record<string, unknown>) => void;
  updateUserSettings: (key: string, value: unknown) => void;
  loadUserSettingsFromBackend: () => Promise<void>;
  saveUserSettingsToBackend: () => Promise<void>;

  clearUser: () => void;
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set) => ({
        username: '',
        userInfo: null,
        userSettings: {},

        setUsername: (username) => set({ username }),
        setUserInfo: (info) => set({ userInfo: info }),
        setUserSettings: (settings) => set({ userSettings: settings }),

        updateUserSettings: (key, value) => {
          set((state) => {
            const updatedSettings = { ...state.userSettings, [key]: value };
            console.log('Updating user setting:', key, '=', value);
            console.log('Updated settings state:', updatedSettings);
            return { userSettings: updatedSettings };
          });
          // Trigger backend save asynchronously
          useUserStore.getState().saveUserSettingsToBackend();
        },

        loadUserSettingsFromBackend: async () => {
          try {
            const response = await userApi.fetchSettings();
            const currentSettings = useUserStore.getState().userSettings;
            console.log('Loaded settings from backend:', response.user_settings);
            console.log('Current local settings:', currentSettings);

            // Only update if we got actual settings (not "unknown" string)
            if (response.user_settings && typeof response.user_settings === 'object') {
              // Merge backend settings with current local settings
              // Backend settings take precedence for existing keys
              const mergedSettings = { ...currentSettings, ...response.user_settings };
              console.log('Merged settings:', mergedSettings);
              set({ userSettings: mergedSettings });
            } else if (response.user_settings === 'unknown') {
              // Backend has no settings, save current local settings to backend
              console.log('Backend has no settings, saving local settings');
              if (Object.keys(currentSettings).length > 0) {
                await userApi.updateSettings(currentSettings);
              }
            }
          } catch (error) {
            console.error('Failed to load user settings from backend:', error);
            // Keep using local settings if backend fails
          }
        },

        saveUserSettingsToBackend: async () => {
          try {
            const { userSettings } = useUserStore.getState();
            console.log('Saving settings to backend:', userSettings);
            const response = await userApi.updateSettings(userSettings);
            console.log('Settings saved successfully:', response);
          } catch (error) {
            console.error('Failed to save user settings to backend:', error);
            // Settings are still saved locally via persist middleware
          }
        },

        clearUser: () => set({
          username: '',
          userInfo: null,
          userSettings: {},
        }),
      }),
      {
        name: 'user-storage',
        partialize: (state) => ({
          username: state.username,
          userSettings: state.userSettings,
        }),
      }
    )
  )
);
